import {
  GTMPageViewTrackingParameters,
  HarmonizedTracking,
  InteractionEvent,
  TrackingParameterValue,
} from '@/helpers/GTM/GTMParameters';
import { HeaderTrackingData, TrackingData } from '@/types';

export function isGTMEnabled(): boolean {
  return typeof window !== 'undefined' && !!window.dataLayer;
}

const TRACK_EVENT_PARAMS_DEFAULT: Record<string, string | number | undefined> = {
  interaction_sysFeedback: undefined,
  interaction_error: undefined,
  interaction_containerName: undefined,
  interaction_containerDetail: undefined,
  interaction_element: undefined,
  interaction_mode: undefined,
  interaction_goal: undefined,
  h_interaction_containername_052: undefined,
  h_interaction_element_055: undefined,
  h_interaction_mode_059: undefined,
  h_interaction_goal_054: undefined,
  interaction_actor: undefined,
  lead_type: undefined,
  tech_leadId: undefined,
  client_sns_subscriptionId: undefined,
  page_targetUrl: undefined,
  page_teaserName_displayed: undefined,
};

/**
 * @deprecated use trackEventHarmonized instead
 */
export function trackEvent(
  eventName: string,
  eventParams: Record<string, TrackingParameterValue> = {},
): void {
  if (!isGTMEnabled()) {
    return;
  }
  window.dataLayer.push({
    ...TRACK_EVENT_PARAMS_DEFAULT,
    listing_status: undefined,
    listing_viewing: undefined,
    listing_geo_tags: undefined,
    ...eventParams,
    event: eventName,
  });
}

export function trackEventWithoutPreDefinedListing(
  eventName: string,
  eventParams: Record<string, TrackingParameterValue> = {},
): void {
  if (!isGTMEnabled()) {
    return;
  }
  window.dataLayer.push({
    ...TRACK_EVENT_PARAMS_DEFAULT,
    ...eventParams,
    event: eventName,
  });
}

export function trackVirtualPageView(
  pageViewTrackingParameters: GTMPageViewTrackingParameters | HarmonizedTracking,
  eventParams: Record<string, TrackingParameterValue> = {},
): void {
  trackEvent(InteractionEvent.LOAD, {
    ...eventParams,
    ...pageViewTrackingParameters,
  });
}

export function trackFooterClickHandler(data: Omit<TrackingData, 'eventInteraction'>): void {
  const dataObj = {
    event: InteractionEvent.FOOTER_INTERACTION,
    h_event_category_006: InteractionEvent.FOOTER_INTERACTION,
    h_event_action_007: data.eventAction,
    h_event_label_008: data.eventLabel,
    non_interaction: false,
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataObj);
}

export function trackHeaderClickHandler(data: HeaderTrackingData): void {
  const dataObj: any = {
    event: 'header_interaction',
    h_event_category_006: 'header_interaction',
    h_event_action_007: data.eventAction,
    h_event_label_008: data.eventLabel,
  };
  if (data.contentLanguage) {
    dataObj['h_content_language_116'] = data.contentLanguage;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataObj);
}

/**
 * Tracks the NEW_IMMOCHECK 2.0 interaction event
 * @param targetUrl - The URL the user is navigating to
 */
export function trackNewImmocheckInteraction(targetUrl: string): void {
  const baseUrl = import.meta.env.VUE_APP_BASE_URL as string;
  const isInternalUrl = targetUrl.startsWith(baseUrl);

  const dataObj: any = {
    event: 'pdp_interaction',
    h_event_action_007: 'go_to_ubs_immocheck',
    h_event_label_008: 'pdp-content',
  };

  // Only add target URL if it's external (not our domain)
  if (!isInternalUrl) {
    dataObj['h_target_url_029'] = targetUrl;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataObj);
}
/**
 * Functions interacting directly with dataLayer.
 */
export function trackEventHarmonized(
  eventName: string,
  eventParams: HarmonizedTracking = {},
): void {
  if (!isGTMEnabled()) {
    return;
  }

  setTimeout(() => {
    if (!isGTMEnabled()) {
      return;
    }
    window.dataLayer.push({
      ...eventParams,
      event: eventName,
    });
  });
}
